// @ts-nocheck

import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { type KonvaNodeComponent } from 'react-konva';
import styled, { css } from 'styled-components';

import { Target } from '../../../client';
import colors from '../../../styled-components/palette';
import { useIsFontLoaded, useLoadImages } from '../hooks';

import Canvas from './Canvas';

const HiddenCanvas = styled.div`
  display: none;
`;

const ImgWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 0.6em;
`;

const Img = styled.img`
  &&&& {
    width: auto;
    height: auto;
    max-width: 70%;
    border: solid 1px ${colors.bluegrey800};
    border-radius: 1em;

    ${Target.__MOBILE__
      ? css`
          max-width: 100%;
        `
      : null}
  }
`;

const Preview = styled.div``;

const betshareFonts = {
  BettingPoliticaItalic: {},
  BettingPoliticaExtraBold: {},
  BettingPoliticaExtraBoldItalic: {},
};

type BetshareProps = {
  className?: string;
  outcomesDetails: Array<unknown>;
  betslipReference: string;
  assets: unknown;
  canvasAreas: unknown;
  needAskBetslipInfo: boolean;
  askBetslipInfo: () => void;
  options?: {
    nickname?: string | null;
    stack?: boolean;
    showPseudo?: boolean;
  };
};

const BetshareImage = ({
  className,
  outcomesDetails,
  betslipReference,
  assets,
  canvasAreas,
  needAskBetslipInfo,
  askBetslipInfo,
  options = { nickname: null, stack: false, showPseudo: false },
}: BetshareProps) => {
  const refStage = useRef<KonvaNodeComponent>(null);

  const [imageUrl, setImageUrl] = useState(null);
  const [isReadyToShare, setIsReadyToShare] = useState(false);
  const [shouldRetry] = useState(false);

  /**
   * Usage of hooks to check asynchronously if assets (fonts/images) are loaded
   * @param {object}    assetToCheck     The object of assets or fonts we need to check before drawing
   * @param {boolean}   shouldRetry      A boolean passed allowing to retry when user clicks after getting an error
   *
   * @return {object} Returns an object containing a boolean (isAssetLoaded), an error object containing errorType and errorText
   * and for the images, returns the loaded images
   */
  const fontLoadingResponse = useIsFontLoaded(betshareFonts, shouldRetry);
  const isFontLoaded = fontLoadingResponse.isFontLoaded;

  const imagesLoadingResponse = useLoadImages(assets, shouldRetry);
  const isImagesLoaded = imagesLoadingResponse.isImagesLoaded;
  const isImagesLoadedError = imagesLoadingResponse.isImagesLoadedError;
  const images = useMemo(
    () => (!isImagesLoadedError ? imagesLoadingResponse.images : {}),
    [isImagesLoadedError, imagesLoadingResponse],
  );

  const isCanvasMode = useMemo(() => imageUrl === null, [imageUrl]);

  // The display is based on this condition. We are ready to draw when all data, assets and fonts are loaded correctly
  const isReadyToDraw = useMemo(
    () => outcomesDetails.length > 0 && Object.keys(images).length > 0 && isImagesLoaded && isFontLoaded,
    [outcomesDetails.length, images, isImagesLoaded, isFontLoaded],
  );

  useEffect(() => {
    if (needAskBetslipInfo) {
      askBetslipInfo();
    }
  }, [askBetslipInfo, betslipReference, needAskBetslipInfo]);

  useEffect(() => {
    if (isReadyToShare) {
      // we get pixelRatio used by konva, its device pixelRatio but we limit to 4 to avoid assets to be stretched
      const pixelRatio = Math.min(refStage.current.getLayers()[0].getCanvas().pixelRatio, 4);
      const dataURL = refStage.current.toDataURL({
        pixelRatio,
        mimeType: 'image/jpeg',
      });

      setImageUrl(dataURL);
    }
  }, [isReadyToShare, refStage, setImageUrl]);

  const renderShareableImage = useCallback(
    () => (
      <Preview className={className}>
        <ImgWrapper>
          <Img src={imageUrl} alt={`betshare-${betslipReference}`} />
        </ImgWrapper>
      </Preview>
    ),
    [betslipReference, className, imageUrl],
  );

  if (isReadyToDraw) {
    return (
      <>
        <HiddenCanvas>
          <Canvas
            ref={refStage}
            betslipReference={betslipReference}
            outcomesDetails={outcomesDetails}
            images={images}
            nickname={options.nickname}
            showPseudo={options.showPseudo}
            showWinningsAndStake={options.stack}
            setIsReadyToShare={setIsReadyToShare}
            isReadyToShare={isReadyToShare}
            canvasAreas={canvasAreas}
          />
        </HiddenCanvas>

        {!isCanvasMode ? renderShareableImage() : null}
      </>
    );
  }

  return null;
};

export default BetshareImage;
